import React, {useMemo, useState} from 'react';
import {Avatar, Box, Chip, TextField} from '@mui/material';
import {DataGrid, type GridColDef} from '@mui/x-data-grid';
import {DirectionsCar} from '@mui/icons-material';
import {useTranslation} from 'react-i18next';

import {formatDate} from '@/global/helpers/text';
import {apiGetTransportListBySchool} from '@/modules/transports/services/transport.service';
import {type TransportDto, transportDtoStatusEnum} from "@/global/api/gen/ts/TransportDto";

interface Props {
    id: number,
}

function TransportTable({id}: Props) {

    const {t} = useTranslation();

    const [search, setSearch] = useState('');

    const [paginationModel, setPaginationModel] = useState({
        pageSize: 25,
        page: 0
    });

    const {data, isLoading} = apiGetTransportListBySchool(id, {
        offset: 0, limit: 999
    });

    const filteredRows = useMemo(() => {
        if (!data?.items) return [];
        const searchLowerCase = search.toLowerCase();
        return data.items.filter((row) => {
            if (row.model?.toLowerCase().includes(searchLowerCase)) {
                return true;
            }
            if (row.brand?.toLowerCase().includes(searchLowerCase)) {
                return true;
            }
            if (row.id.toString().includes(search)) {
                return true;
            }
            return !!row.plate_number?.toLowerCase().includes(search);
        });
    }, [data, search]);

    const filteredRowsCount = filteredRows.length;

    const columns: GridColDef<TransportDto>[] = useMemo(() => [
        {field: 'empty', headerName: '', headerClassName: 'datagrid--header', maxWidth: 10, width: 10, sortable: false},
        {field: 'id', headerName: 'ID', width: 110, sortable: true},
        {
            field: 'model',
            headerName: t('school_page.transport_page.transport_table.grid.model'),
            minWidth: 360,
            flex: 1,
            sortable: true,
            renderCell: ({row}) => {
                return (
                    <React.Fragment>
                        <Avatar
                            sx={{mr: 1.5, fontSize: 16, color: '#99999F', bgcolor: '#D9E8FF'}}
                        >
                            <DirectionsCar/>
                        </Avatar>
                        {row.brand}
                        {row.model}
                    </React.Fragment>
                );
            }
        },
        {
            field: 'status', headerName: 'Статус', minWidth: 150, sortable: false, renderCell: ({row}) => {
                if (row.status === transportDtoStatusEnum['1']) {
                    return (
                        <Chip
                            color={'primary'}
                            label={t('school_page.employees_page.employee_grid.status.active')}
                        />
                    );
                }
                return (
                    <Chip
                        color={'default'}
                        label={t('school_page.employees_page.employee_grid.status.inactive')}
                    />
                );
            }
        },
        {
            field: 'plate_number',
            headerName: t('school_page.transport_page.transport_table.grid.plate_number'),
            minWidth: 160
        },
        {field: 'category', headerName: t('school_page.transport_page.transport_table.grid.category'), minWidth: 160},
        {
            field: 'description',
            headerName: t('school_page.transport_page.transport_table.grid.description'),
            minWidth: 160
        },
        {
            field: 'created_at',
            headerName: t('school_page.transport_page.transport_table.grid.created'),
            minWidth: 200,
            valueFormatter: ({value}) => formatDate(value)
        },
    ], [t]);

    return (
        <Box sx={{mt: 2, display: 'flex', flexDirection: 'column', gap: 2}}>
            <TextField
                label={'Поиск по таблице'}
                onChange={(e) => {
                    const {value} = e.target;
                    setSearch(value);
                }}
            />
            <DataGrid
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                rows={filteredRows ?? []}
                // onRowClick={handleRowClick}
                columns={columns}
                loading={isLoading}
                rowCount={filteredRowsCount ?? 0}
                disableRowSelectionOnClick
                disableColumnMenu={true}
                disableColumnFilter={true}
            />
        </Box>
    );
}

export default TransportTable;